<script setup>
  import { ref, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import DataContainer from '@/components/DataContainer.vue';
  import DataContainerSection from '@/components/DataContainerSection.vue';
  import CopyContent from '@/components/CopyContent.vue';
  import WidgetPreview from '@/components/WidgetPreview.vue';
  import Icon from '@/components/icons/Icon.vue';
  import { useToaster } from '@/composables/useToaster';

  const props = defineProps({
    title: {
      type: String,
      required: true
    },
    // Embed code copied by the "Copy code" button
    code: {
      type: String,
      default: ''
    },
    // The widget's real size; the preview scales it down to fit the block
    previewWidth: {
      type: Number,
      required: true
    },
    previewHeight: {
      type: Number,
      required: true
    }
  });

  const emit = defineEmits(['customize', 'copied']);

  const toaster = useToaster();
  const { t } = useI18n();

  const previewScale = ref(1);

  const previewPercent = computed(() => Math.round(previewScale.value * 100));

  // The widget's shape, for the one-column gallery, where each block is only as tall as its widget
  const previewShape = computed(() => ({
    '--jwx-widget-block-preview-ratio': `${props.previewWidth} / ${props.previewHeight}`,
    '--jwx-widget-block-preview-max-height': `${props.previewHeight}px`
  }));

  function handleCustomize() {
    emit('customize');
  }

  function handleCopied() {
    emit('copied');
    toaster.toast({
      type: 'success',
      content: t('widgets.codeCopied')
    });
  }
</script>

<template>
  <DataContainer class="jwx-widget-block">
    <DataContainerSection :sectionOverflow="false" :headerHeightOff="true">
      <template #title>{{ props.title }}</template>
      <!-- Only shown when the preview is scaled down -->
      <template v-if="previewPercent < 100" #metaActions>
        <span
          class="jwx-chip jwx-chip--neutral"
          :title="t('widgets.previewScale', { percent: previewPercent })"
        >
          <span class="jwx-chip__text" aria-hidden="true">{{ t('widgets.previewScaleChip', { percent: previewPercent }) }}</span>
          <span class="sr-only">{{ t('widgets.previewScale', { percent: previewPercent }) }}</span>
        </span>
      </template>

      <template #content>
        <WidgetPreview
          v-model:scale="previewScale"
          :width="props.previewWidth"
          :height="props.previewHeight"
          :style="previewShape"
        >
          <slot name="preview"></slot>
        </WidgetPreview>
      </template>

      <template #footer>
        <div class="jwx-widget-block__actions">
          <button
            @click="handleCustomize"
            type="button"
            class="jwx-button-control jwx-widget-block__action"
            data-surface="0"
            data-shape="pill"
          >
            <Icon name="sliders" class="jwx-button__icon" />
            {{ t('widgets.customize') }}
          </button>
          <CopyContent @copied="handleCopied">
            <template #default="{ copy }">
              <button
                @click="copy(props.code)"
                :disabled="!props.code"
                type="button"
                class="jwx-button-control jwx-widget-block__action"
                data-surface="0"
                data-shape="pill"
              >
                <Icon name="clipboard" class="jwx-button__icon" />
                {{ t('widgets.copyEmbed') }}
              </button>
            </template>
          </CopyContent>
        </div>
      </template>
    </DataContainerSection>
  </DataContainer>
</template>
